import { useState } from "react";
import { GENRES, numJP, type LibraryStatus } from "../data";
import type { Nav, ToastInfo } from "../types";
import { Empty, Footer, LoadingBlock, Placeholder } from "../components/atoms";
import { useFetch } from "../hooks";
import { fetchMyLibrary } from "../api";
import { useT } from "../i18n";

interface Props {
  nav: Nav;
  onToast?: (t: ToastInfo) => void;
}

type Filter = "all" | LibraryStatus;

export function LibraryPage({ nav, onToast }: Props) {
  const t = useT();
  const [filter, setFilter] = useState<Filter>("all");
  const libFetch = useFetch(fetchMyLibrary, []);

  if (libFetch.loading) {
    return (
      <div className="page page-library">
        <div className="container-narrow" style={{ marginTop: 20 }}>
          <LoadingBlock height={400} />
        </div>
      </div>
    );
  }

  const all = libFetch.data ?? [];
  const counts: Record<Filter, number> = {
    all: all.length,
    unplayed: all.filter((l) => l.status === "unplayed").length,
    "in-progress": all.filter((l) => l.status === "in-progress").length,
    cleared: all.filter((l) => l.status === "cleared").length,
  };
  const items = all
    .filter((l) => filter === "all" || l.status === filter)
    .filter((x) => x.work);

  return (
    <div className="page page-library">
      <div className="container-narrow" style={{ marginTop: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--accent)", fontWeight: 800 }}>
          {t("library.title")}
        </h1>
        <div
          style={{
            color: "var(--muted-2)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            marginTop: 4,
          }}
        >
          {t("library.summary", {
            total: numJP(all.length),
            inprog: counts["in-progress"],
            unplayed: counts.unplayed,
          })}
        </div>

        <div className="library-filter">
          {(
            [
              { k: "all", l: t("library.filterAll") },
              { k: "unplayed", l: t("library.filterUnplayed") },
              { k: "in-progress", l: t("library.filterInProgress") },
              { k: "cleared", l: t("library.filterCleared") },
            ] as Array<{ k: Filter; l: string }>
          ).map((tab) => (
            <button
              key={tab.k}
              className={`lftab ${filter === tab.k ? "active" : ""}`}
              onClick={() => setFilter(tab.k)}
            >
              {tab.l} <span className="count">{counts[tab.k]}</span>
            </button>
          ))}
        </div>

        {items.length > 0 ? (
          <div className="library-list">
            {items.map((entry) => {
              const work = entry.work!;
              return (
                <div className="lib-item" key={work.id}>
                  <Placeholder
                    caption=""
                    tone={GENRES[work.genre]?.tone}
                    seed={work.id.charCodeAt(2)}
                    className="lib-thumb"
                  />
                  <div
                    className="lib-body"
                    onClick={() => nav({ page: "work", id: work.id })}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="lib-title">{work.title}</div>
                    <div className="lib-author">
                      {work.authorName ?? ""} · {GENRES[work.genre]?.ja}
                    </div>
                    <div className="lib-meta">
                      <span>{t("library.takenAt", { date: entry.purchasedAt })}</span>
                      {entry.isOwn && (
                        <span style={{ color: "var(--success)" }}>
                          {t("library.statusOwn")}
                        </span>
                      )}
                      {entry.status === "cleared" && (
                        <span style={{ color: "var(--success)" }}>
                          {t("library.statusCleared")}
                        </span>
                      )}
                      {entry.status === "in-progress" && (
                        <span style={{ color: "var(--primary)" }}>
                          {t("library.statusInProgress")}
                        </span>
                      )}
                      {entry.status === "unplayed" && (
                        <span style={{ color: "var(--muted-2)" }}>
                          {t("library.statusUnplayed")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="lib-progress">
                    <div className="lp-label">
                      <span>{t("library.progress")}</span>
                      <span>{Math.round(entry.progress * 100)}%</span>
                    </div>
                    <div className="lp-bar">
                      <div
                        className={`lp-fill ${entry.status === "cleared" ? "done" : ""}`}
                        style={{ width: `${entry.progress * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="lib-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() =>
                        onToast?.({
                          kind: "info",
                          text: t("work.engineStarting"),
                        })
                      }
                    >
                      ▶{" "}
                      {entry.status === "unplayed"
                        ? t("library.btnPlay")
                        : entry.status === "in-progress"
                          ? t("library.btnResume")
                          : t("library.btnReplay")}
                    </button>
                    {entry.isOwn && (
                      <button
                        className="btn btn-sm"
                        onClick={() =>
                          onToast?.({
                            kind: "info",
                            text: t("dash.studioMove"),
                          })
                        }
                      >
                        {t("library.openInStudio")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty
            title={t("library.empty")}
            desc={t("library.emptyDesc")}
            action={
              <button
                className="btn btn-accent btn-sm"
                onClick={() => nav({ page: "search" })}
              >
                {t("library.findWorks")}
              </button>
            }
          />
        )}

        <div
          style={{
            marginTop: 40,
            padding: 18,
            background: "var(--panel)",
            border: "1px dashed var(--border)",
            borderRadius: "var(--r-md)",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 22, color: "var(--accent)" }}>
            ✎
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: "var(--text)" }}>
              {t("library.invitationTitle")}
            </div>
            <div
              style={{
                color: "var(--muted-2)",
                fontSize: 11,
                fontFamily: "var(--font-mono)",
                marginTop: 2,
              }}
            >
              {t("library.invitationDesc")}
            </div>
          </div>
          <button
            className="btn btn-accent"
            onClick={() => onToast?.({ kind: "info", text: t("dash.studioMove") })}
          >
            {t("home.studioCreate")}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
